import { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { EndorsementEditor } from "@/components/EndorsementEditor";
import { motion, AnimatePresence } from "framer-motion";
import { imageUrl, api } from "@/lib/api";
import { startExport } from "@/lib/exports";
import { toast } from "sonner";
import { buildSpreads, computeDims } from "@/lib/paginate";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PuzzleView } from "@/components/PuzzleView";
import { BrandFlourish } from "@/components/BrandFlourish";
import { Switch } from "@/components/ui/switch";
import { WrapPreview } from "@/components/WrapPreview";
import { CoverDesigns } from "@/components/CoverDesigns";
import { SavedCovers } from "@/components/SavedCovers";
import { InteriorPreview } from "@/components/InteriorPreview";
import { ShareControls } from "@/components/ShareControls";
import { ContinuityReportV2 } from "@/components/ContinuityReportV2";

const ACTIVITY_MODES = ["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"];
const PUZZLE_MODES = ["sudoku", "maze", "coloring", "wordsearch"];
import {
  ChevronLeft, ChevronRight, FileText, FileType2, FileCode2,
  List, BookMarked, Palette, ArrowLeft, Pencil, Check, X,
  RefreshCw, Loader2, Headphones, Play, Download, Plus, Trash2, Printer, Package, Sparkles, BookOpen, Wand2,
  Megaphone, Copy, Tag, FolderTree, Lock, ShieldCheck, AlertTriangle, Image as ImageIcon,
} from "lucide-react";

const THEMES = ["paper-light", "paper-sepia", "paper-dark"];
const THEME_LABEL = { "paper-light": "Ivory", "paper-sepia": "Sepia", "paper-dark": "Night" };

const renderContent = (text) => {
  const blocks = [];
  const lines = (text || "").split("\n");
  let list = null; // {ordered, items:[]}
  const flushList = (key) => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`l${key}`} className={list.ordered ? "list-decimal pl-6 space-y-1.5 my-3" : "list-disc pl-6 space-y-1.5 my-3"}>
        {list.items.map((html, j) => <li key={j} dangerouslySetInnerHTML={{ __html: html }} />)}
      </Tag>
    );
    list = null;
  };
  const inline = (s) => s
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>");
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flushList(i); return; }
    const bullet = /^[-*]\s+(.*)/.exec(line);
    const numbered = /^\d+\.\s+(.*)/.exec(line);
    if (bullet) {
      if (!list || list.ordered) { flushList(i); list = { ordered: false, items: [] }; }
      list.items.push(inline(bullet[1]));
      return;
    }
    if (numbered) {
      if (!list || !list.ordered) { flushList(i); list = { ordered: true, items: [] }; }
      list.items.push(inline(numbered[1]));
      return;
    }
    flushList(i);
    if (line.startsWith("> ")) {
      const t = line.slice(2).replace(/^(\*\*)?pro tip:?(\*\*)?\s*/i, "");
      blocks.push(
        <div key={i} className="my-4 rounded-lg border-l-4 border-gold-500 bg-gold-50/70 px-4 py-3">
          <div className="font-ui text-[11px] font-bold uppercase tracking-[0.18em] text-gold-700 mb-1">Pro Tip</div>
          <p className="!my-0" dangerouslySetInnerHTML={{ __html: inline(t) }} />
        </div>
      );
    } else if (line.startsWith("### ")) {
      blocks.push(<h3 key={i}>{line.replace(/^#+\s*/, "")}</h3>);
    } else if (line.startsWith("## ") || line.startsWith("# ")) {
      blocks.push(<h2 key={i}>{line.replace(/^#+\s*/, "")}</h2>);
    } else {
      blocks.push(<p key={i} dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList("end");
  return blocks;
};

// Drop a leading markdown heading that merely repeats the chapter title or a "Chapter N" label.
const stripDupHeading = (content, title = "") => {
  if (!content) return content;
  const lines = content.split("\n");
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i += 1;
  if (i >= lines.length || !lines[i].trimStart().startsWith("#")) return content;
  const heading = lines[i].replace(/^\s*#+\s*/, "").trim();
  const norm = (s) => (s || "").toLowerCase().replace(/^[\s.:–—"'-]+|[\s.:–—"'-]+$/g, "");
  const h = norm(heading);
  const t = norm(title);
  const hNoCh = h.replace(/^chapter\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\s*[:.\u2013\u2014-]?\s*/, "").trim();
  const isChapterLabel = /^chapter\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\b/.test(h);
  const matchesTitle = !!t && (h === t || hNoCh === t);
  if (isChapterLabel || matchesTitle) {
    lines.splice(i, 1);
    while (i < lines.length && !lines[i].trim()) lines.splice(i, 1);
    return lines.join("\n").trim();
  }
  return content;
};

const editInput =
  "w-full bg-black/5 border border-current/20 rounded-lg px-3 py-2 font-read outline-none focus:border-gold-500";

const stripMd = (s) => (s || "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1");

export const BookReader = ({
  book, onBack, onRegenChapter, onRegenCover, onUpdateBook, onBookReplaced,
  onAddChapter, onDeleteChapter, onGenerateAudio,
  onRewriteChapter, onCoverOptions, onSelectCover, onApplyDesign, onRestoreCover, onGenerateMarketing, user, onBuyPublish,
  onContinuityCheck, onCompleteBook, onShowcase,
}) => {
  const [page, setPage] = useState(0);
  const [theme, setTheme] = useState("paper-light");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showAudio, setShowAudio] = useState(false);
  const [showMarketing, setShowMarketing] = useState(false);
  const [marketingBusy, setMarketingBusy] = useState(false);
  const [showContinuity, setShowContinuity] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addFocus, setAddFocus] = useState("");
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteText, setRewriteText] = useState("");
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverStyle, setCoverStyle] = useState("Painterly");
  const [printOpen, setPrintOpen] = useState(false);
  const [trims, setTrims] = useState([]);
  const [trimKey, setTrimKey] = useState(book.trim_key || (book.mode === "children" ? "8.5x8.5" : "6x9"));
  const [bleed, setBleed] = useState(true);

  const hasToc = book.show_toc !== false;
  const tocOffset = hasToc ? 2 : 1;
  const totalPages = book.chapters.length + tocOffset;   // legacy (edit-mode) page model
  const isPuzzle = ["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"].includes(book.mode);
  const busy = book.busy;
  const audioStatus = book.audio_status || "none";

  // ---- Book Preview (physical pagination) ----
  const [spreads, setSpreads] = useState(null);
  const [pageW, setPageW] = useState(560);
  const containerRef = useRef(null);
  const measurerRef = useRef(null);
  const pendingChapterRef = useRef(null);

  const trim = useMemo(
    () => trims.find((t) => t.key === trimKey) || { w: book.mode === "children" ? 8.5 : 6, h: book.mode === "children" ? 8.5 : 9 },
    [trims, trimKey, book.mode]
  );
  const dims = useMemo(() => computeDims(trim, pageW), [trim, pageW]);

  const contentSig = useMemo(
    () => JSON.stringify((book.chapters || []).map((c) => [c.number, c.title, c.content, c.image_id, c.caption]))
      + "|" + book.show_toc + "|" + book.mode + "|" + book.cover_image_id,
    [book.chapters, book.show_toc, book.mode, book.cover_image_id]
  );

  // fit the physical page to the available canvas width
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setPageW(Math.max(300, Math.min(600, Math.round((el.clientWidth || 600) - 8))));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // recompute spreads whenever content / trim / layout changes (never while editing)
  useLayoutEffect(() => {
    if (editing) return;
    const m = measurerRef.current;
    if (!m) return;
    m.style.width = dims.textW + "px";
    m.style.fontSize = dims.bodyPx + "px";
    m.style.lineHeight = dims.linePx + "px";
    const built = buildSpreads(book, m, dims, { hasToc, isPuzzle, stripDupHeading });
    setSpreads(built);
    if (pendingChapterRef.current != null) {
      const ci = pendingChapterRef.current;
      pendingChapterRef.current = null;
      const idx = built.list.findIndex((s) => s.type === "page" && s.chapterIndex === ci);
      setPage(idx >= 0 ? idx : 0);
    } else {
      setPage((p) => Math.min(p, built.list.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, contentSig, dims, hasToc, isPuzzle]);

  const currentSpread = !editing && spreads ? spreads.list[Math.min(page, spreads.list.length - 1)] : null;
  const chapterIndex = editing ? page - tocOffset : (currentSpread?.chapterIndex ?? -1);
  const navCount = editing ? totalPages : (spreads ? spreads.list.length : 1);

  useEffect(() => {
    if (busy && editing) setEditing(false);
  }, [busy]); // eslint-disable-line

  useEffect(() => {
    api.meta().then((m) => setTrims(m.kdp_trims || [])).catch(() => {});
  }, []);

  const startEdit = () => {
    // jump the legacy view to the chapter currently on screen
    const ci = currentSpread?.chapterIndex;
    setPage(ci != null && ci >= 0 ? ci + tocOffset : (currentSpread?.type === "toc" ? 1 : 0));
    setDraft({
      title: book.title,
      subtitle: book.subtitle || "",
      author: book.author || "",
      author_bio: book.author_bio || "",
      chapters: Object.fromEntries(
        book.chapters.map((c) => [c.number, { title: c.title, content: c.content, caption: c.caption || "" }])
      ),
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    pendingChapterRef.current = page >= tocOffset ? page - tocOffset : null;
    setEditing(false);
    setDraft(null);
  };

  const saveEdit = () => {
    const patch = {
      title: draft.title,
      subtitle: draft.subtitle,
      author: draft.author,
      author_bio: draft.author_bio,
      chapters: Object.entries(draft.chapters).map(([number, v]) => ({
        number: Number(number),
        title: v.title,
        content: v.content,
        caption: v.caption,
      })),
    };
    onUpdateBook(patch);
    pendingChapterRef.current = page >= tocOffset ? page - tocOffset : null;
    setEditing(false);
    setDraft(null);
  };

  const setChapterField = (number, field, value) =>
    setDraft((d) => ({
      ...d,
      chapters: { ...d.chapters, [number]: { ...d.chapters[number], [field]: value } },
    }));

  const runExport = (fmt, args = {}) =>
    startExport(book.id, fmt, args)
      .then(() => toast.success("Export started: it downloads automatically when ready"))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not start export"));
  const download = (fmt) => runExport(fmt);

  const publishUnlocked = !!(book.publish_unlocked || !book.is_preview || user?.role === "admin");
  const getPremiumExport = (fmt, args) => {
    if (!publishUnlocked) { onBuyPublish && onBuyPublish(); return; }
    runExport(fmt, args);
  };

  const goTo = (p) => setPage(Math.max(0, Math.min(navCount - 1, p)));
  const gotoCover = () => goTo(0);
  const gotoChapter = (i) => {
    if (editing) { setPage(i + tocOffset); return; }
    const idx = spreads?.list?.findIndex((s) => s.type === "page" && s.chapterIndex === i);
    setPage(idx >= 0 ? idx : 0);
  };

  const submitAddChapter = async () => {
    const after = chapterIndex >= 0 ? chapterIndex + 1 : book.chapters.length;
    setAddOpen(false);
    pendingChapterRef.current = after;
    await onAddChapter(after, addTitle, addFocus);
    setAddTitle("");
    setAddFocus("");
    if (editing) goTo(after + tocOffset);
  };

  const removeChapter = async (number) => {
    const unit = ACTIVITY_MODES.includes(book.mode) ? "page" : book.mode === "children" ? "page" : "chapter";
    if (!window.confirm(`Delete ${unit} ${number}? This can't be undone.`)) return;
    pendingChapterRef.current = Math.max(0, chapterIndex - 1);
    await onDeleteChapter(number);
  };

  const footerLabel = useMemo(() => {
    if (editing) {
      if (page === 0) return "Cover";
      if (hasToc && page === 1) return "Contents";
      if (ACTIVITY_MODES.includes(book.mode)) return book.chapters[page - tocOffset]?.title || "";
      return `${book.mode === "children" ? "Page" : "Chapter"} ${page - tocOffset + 1}`;
    }
    if (!currentSpread) return "";
    if (currentSpread.type === "cover") return "Cover";
    if (currentSpread.type === "toc") return "Contents";
    const total = spreads?.totalManu || 1;
    if (currentSpread.mode === "prose")
      return `Chapter ${currentSpread.chapterNumber} · Page ${currentSpread.manuscriptPage} of ${total}`;
    return `Page ${currentSpread.manuscriptPage} of ${total}`;
  }, [editing, page, hasToc, tocOffset, book.mode, currentSpread, spreads]);

  return (
    <div className="max-w-7xl mx-auto pt-4 pb-24">
      {/* Top action bar */}
      <div
        data-testid="export-bar"
        className="flex flex-wrap items-center justify-between gap-3 mb-6 p-4 bg-navy-900/90 border border-gold-500/12 rounded-xl"
      >
        <button
          data-testid="reader-back-button"
          onClick={onBack}
          className="flex items-center gap-2 font-ui text-sm text-slate-300 hover:text-gold-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> New book
        </button>
        {onShowcase && !editing && (
          <button
            data-testid="reader-visualize-button"
            onClick={onShowcase}
            className="flex items-center gap-2 font-ui text-sm text-gold-200 hover:text-gold-100 transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Visualize
          </button>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {book.status === "done" && (
            <label
              data-testid="edit-mode-toggle"
              className={`flex items-center gap-2.5 font-ui text-sm font-medium rounded-lg px-3 py-2 border transition-colors select-none ${editing ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-gold-500/12 text-slate-200"} ${busy && !editing ? "opacity-40" : "cursor-pointer"}`}
              title="Turn on to edit any title, word, caption or page — then turn off (or Save) to keep your changes."
            >
              <Pencil className="w-4 h-4 text-slate-400" />
              Edit mode
              <Switch
                data-testid="edit-mode-switch"
                checked={editing}
                disabled={busy && !editing}
                onCheckedChange={(v) => (v ? startEdit() : saveEdit())}
              />
            </label>
          )}
          {editing ? (
            <>
              <button
                data-testid="reader-save-button"
                onClick={saveEdit}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-stone-950 rounded-lg px-4 py-2 transition-colors"
              >
                <Check className="w-4 h-4" /> Save
              </button>
              <button
                data-testid="reader-cancel-edit"
                onClick={cancelEdit}
                className="flex items-center gap-2 font-ui text-sm text-slate-300 border border-gold-500/12 hover:border-red-500/60 rounded-lg px-3 py-2 transition-colors"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="reader-theme-toggle"
                onClick={() => setTheme(THEMES[(THEMES.indexOf(theme) + 1) % 3])}
                className="flex items-center gap-2 font-ui text-sm text-slate-200 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-3 py-2 transition-colors"
              >
                <Palette className="w-4 h-4" /> {THEME_LABEL[theme]}
              </button>
              <select
                data-testid="reader-trim-select"
                value={trimKey}
                onChange={(e) => setTrimKey(e.target.value)}
                title="Preview trim size"
                className="font-ui text-sm text-slate-200 bg-navy-900 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-2.5 py-2 outline-none cursor-pointer"
              >
                {(trims.length ? trims : [{ key: trimKey, label: trimKey }]).map((t) => (
                  <option key={t.key} value={t.key} className="bg-navy-900">{t.label}</option>
                ))}
              </select>
              {book.status === "done" && <InteriorPreview book={book} trims={trims} trimKey={trimKey} />}
              {book.status === "done" && !book.is_preview && <ShareControls book={book} onChange={onBookReplaced} />}
              <button
                data-testid="reader-toc-toggle"
                onClick={() => { onUpdateBook({ show_toc: !hasToc }); if (hasToc && page >= 1) setPage(0); }}
                className={`flex items-center gap-2 font-ui text-sm border rounded-lg px-3 py-2 transition-colors ${hasToc ? "text-slate-300 border-gold-400/60" : "text-slate-300 border-gold-500/12 hover:border-gold-400/60"}`}
              >
                <List className="w-4 h-4" /> TOC {hasToc ? "On" : "Off"}
              </button>
              {book.mode === "maze" && book.answer_keys_per_page && (
                <select
                  data-testid="reader-answer-keys-select"
                  value={book.answer_keys_per_page}
                  onChange={(e) => onUpdateBook({ answer_keys_per_page: Number(e.target.value) })}
                  title="Answer keys per page"
                  className="font-ui text-sm text-slate-200 bg-navy-900 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-2.5 py-2 outline-none cursor-pointer"
                >
                  <option value={4} className="bg-navy-900">Keys: 4 per page</option>
                  <option value={2} className="bg-navy-900">Keys: 2 per page</option>
                </select>
              )}
              <div className="hidden sm:block w-px h-6 bg-navy-800 mx-1" />
              <button
                data-testid="export-pdf-button"
                onClick={() => download("pdf")}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-lg px-4 py-2 transition-colors"
              >
                <FileText className="w-4 h-4" /> PDF
              </button>
              <button
                data-testid="export-print-pdf-button"
                onClick={() => (publishUnlocked ? setPrintOpen(true) : onBuyPublish && onBuyPublish())}
                className="flex items-center gap-2 font-ui text-sm text-slate-200 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-3 py-2 transition-colors"
              >
                {publishUnlocked ? <Printer className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5 text-slate-400" />} Print
              </button>
              <button
                data-testid="export-docx-button"
                onClick={() => download("docx")}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-stone-100 hover:bg-white text-stone-900 rounded-lg px-4 py-2 transition-colors"
              >
                <FileType2 className="w-4 h-4" /> Word
              </button>
              <button
                data-testid="export-markdown-button"
                onClick={() => download("markdown")}
                className="flex items-center gap-2 font-ui text-sm text-slate-300 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-3 py-2 transition-colors"
              >
                <FileCode2 className="w-4 h-4" /> .md
              </button>
              <button
                data-testid="export-epub-button"
                onClick={() => getPremiumExport("epub")}
                className="flex items-center gap-2 font-ui text-sm text-slate-200 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-3 py-2 transition-colors"
              >
                {publishUnlocked ? <BookOpen className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5 text-slate-400" />} EPUB
              </button>
              <button
                data-testid="export-kdp-button"
                onClick={() => (publishUnlocked ? setPrintOpen(true) : onBuyPublish && onBuyPublish())}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-500 hover:bg-gold-400 text-white rounded-lg px-4 py-2 transition-colors"
              >
                {publishUnlocked ? <Package className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />} Publishing Kit
              </button>
              <button
                data-testid="audiobook-button"
                onClick={() => setShowAudio((s) => !s)}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 transition-colors"
              >
                <Headphones className="w-4 h-4" /> Audiobook
              </button>
              <button
                data-testid="marketing-kit-button"
                onClick={() => (publishUnlocked ? setShowMarketing((s) => !s) : onBuyPublish && onBuyPublish())}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-500 hover:bg-gold-400 text-white rounded-lg px-4 py-2 transition-colors"
              >
                {publishUnlocked ? <Megaphone className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />} Marketing Kit
              </button>
              <button
                data-testid="continuity-check-button"
                onClick={() => setShowContinuity((s) => !s)}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg px-4 py-2 transition-colors"
              >
                <ShieldCheck className="w-4 h-4" /> Continuity
                {book.continuity_report?.flagged > 0 && (
                  <span className="bg-amber-400 text-stone-950 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {book.continuity_report.flagged}
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {book.is_preview && !editing && (
        <div data-testid="preview-banner" className="mb-6 flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10">
          <div className="flex items-center gap-2 font-read text-sm text-amber-100">
            <Lock className="w-4 h-4 text-amber-300 shrink-0" />
            Free preview — cover, table of contents &amp; the first {book.mode === "children" ? "page" : "chapter"}. Unlock with one book credit to write the whole book. It includes the complete Amazon KDP package.
          </div>
          <button
            data-testid="unlock-full-book-button"
            onClick={() => onCompleteBook && onCompleteBook()}
            className="klyr-btn-metal font-ui text-sm font-semibold rounded-lg px-4 py-2 transition-[filter]"
          >
            Unlock the full book
          </button>
        </div>
      )}

      {showAudio && !editing && (
        <AudiobookBar book={book} onGenerate={onGenerateAudio} />
      )}

      {showMarketing && !editing && (
        <MarketingBar
          book={book}
          onBookReplaced={onBookReplaced}
          busy={marketingBusy}
          onGenerate={async () => {
            setMarketingBusy(true);
            await onGenerateMarketing();
            setMarketingBusy(false);
          }}
        />
      )}

      {showContinuity && !editing && (
        <ContinuityBar
          book={book}
          busy={busy}
          onRun={onContinuityCheck}
          onGoToChapter={(n) => setPage(n - 1 + tocOffset)}
          onRegenChapter={onRegenChapter}
          onRewriteChapter={onRewriteChapter}
          onBookReplaced={onBookReplaced}
        />
      )}

      {busy && (
        <div
          data-testid="reader-busy-banner"
          className="flex items-center justify-center gap-3 mb-6 py-3 bg-gold-400/10 border border-gold-400/30 rounded-xl font-ui text-sm text-slate-300"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          {book.current_step || "Working…"}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* TOC sidebar */}
        {hasToc && (
        <aside className="lg:col-span-3">
          <div className="bg-navy-900/80 border border-gold-500/12 rounded-2xl p-5 sticky top-24">
            <div className="flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] text-slate-400/90 mb-4">
              <List className="w-4 h-4" /> Contents
            </div>
            <button
              onClick={gotoCover}
              className={`w-full text-left font-read text-sm py-2 px-2 rounded transition-colors ${(editing ? page === 0 : currentSpread?.type === "cover") ? "text-gold-300 bg-gold-400/10" : "text-slate-400 hover:text-slate-200"}`}
            >
              Cover
            </button>
            <div className="space-y-0.5 max-h-[60vh] overflow-y-auto scroll-thin">
              {book.chapters.map((ch, i) => (
                <button
                  key={ch.number}
                  data-testid="reader-toc-link"
                  onClick={() => gotoChapter(i)}
                  className={`w-full text-left font-read text-sm py-2 px-2 rounded flex gap-2 transition-colors ${
                    chapterIndex === i ? "text-gold-300 bg-gold-400/10" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <span className="text-slate-500 font-mono2 text-xs mt-0.5">{ch.number}</span>
                  <span className="leading-snug">{ch.title}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
        )}

        {/* Reader canvas */}
        <main className={hasToc ? "lg:col-span-9" : "lg:col-span-12"}>
          {/* hidden measurer used to flow text into physical pages */}
          <div ref={measurerRef} className="kbp" aria-hidden="true"
            style={{ position: "absolute", visibility: "hidden", left: -99999, top: 0, pointerEvents: "none" }} />

          {editing ? (
            <div
              data-testid="book-reader-container"
              className={`${theme} relative rounded-2xl border border-gold-500/12 shadow-2xl min-h-[70vh] transition-colors duration-500 overflow-hidden`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={page + "-edit"}
                  initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="p-8 sm:p-12 lg:p-16"
                >
                  {page === 0 && (
                    <CoverPage book={book} editing={editing} draft={draft} setDraft={setDraft}
                      busy={busy} onRegenCover={onRegenCover} onCoverStudio={() => setCoverOpen(true)} onSelectCover={onSelectCover} />
                  )}
                  {hasToc && page === 1 && <TocPage book={book} onGo={gotoChapter} />}
                  {page >= tocOffset && isPuzzle ? (
                    <PuzzleView chapter={book.chapters[chapterIndex]} />
                  ) : page >= tocOffset && (
                    <ChapterPage
                      chapter={book.chapters[chapterIndex]}
                      editing={editing} draft={draft} setChapterField={setChapterField}
                      busy={busy} onRegenChapter={onRegenChapter}
                      onAdd={() => setAddOpen(true)} onDelete={removeChapter}
                      canDelete={book.chapters.length > 1} isChildren={book.mode === "children"}
                      onRewrite={() => { setRewriteText(""); setRewriteOpen(true); }}
                      onHumanize={() => onRewriteChapter(chapterIndex + 1, "Make this sound genuinely human, natural and warm. Remove any stiff, robotic or AI-sounding phrasing and clichés while keeping the same meaning and length.")}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          ) : (
            <div ref={containerRef} data-testid="book-reader-container" className="relative">
              {book.is_preview && currentSpread?.type === "page" && currentSpread.chapterIndex > 0 ? (
                <div data-testid="preview-lock" className="text-center py-16 px-6 rounded-2xl border border-gold-500/12 bg-navy-900/60">
                  <Lock className="w-10 h-10 text-amber-300 mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-slate-100">Unlock the full book to keep reading</h3>
                  <p className="font-read text-slate-400 mt-3 max-w-md mx-auto">
                    This is a free preview — you can read the cover, table of contents and the first
                    {book.mode === "children" ? " page" : " chapter"}. Unlock to have KLYR write every remaining
                    {book.mode === "children" ? " page" : " chapter"} as one continuous story.
                  </p>
                  <button data-testid="preview-lock-unlock" onClick={() => onCompleteBook && onCompleteBook()}
                    className="mt-6 klyr-btn-metal font-ui text-sm font-semibold rounded-xl px-6 py-3 transition-[filter]">
                    Unlock the full book
                  </button>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={page}
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <PreviewSpread spread={currentSpread} dims={dims} theme={theme} book={book} />
                  </motion.div>
                </AnimatePresence>
              )}

              {/* chapter controls live OUTSIDE the physical page */}
              {currentSpread?.type === "cover" && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <RerollButton onClick={onRegenCover} busy={busy} testid="reroll-cover-button" label="Reroll cover" />
                  <button data-testid="cover-studio-button" onClick={() => setCoverOpen(true)} disabled={busy}
                    className="inline-flex items-center gap-2 font-ui text-xs font-medium text-slate-200 border border-gold-500/20 hover:border-gold-400 disabled:opacity-40 rounded-full px-4 py-2 transition-colors">
                    <Sparkles className="w-3.5 h-3.5" /> Cover Studio
                  </button>
                </div>
              )}
              {currentSpread?.type === "page" && currentSpread.chapterIndex >= 0 && !(book.is_preview && currentSpread.chapterIndex > 0) && (
                <div data-testid="chapter-actions" className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  {!isPuzzle && <>
                    <RerollButton onClick={() => onRegenChapter(currentSpread.chapterNumber)} busy={busy} testid="reroll-chapter-button" label="Reroll" />
                    <button data-testid="reword-chapter-button" onClick={() => { setRewriteText(""); setRewriteOpen(true); }} disabled={busy}
                      className="inline-flex items-center gap-2 font-ui text-xs font-medium text-slate-200 border border-gold-500/20 hover:border-gold-400 disabled:opacity-40 rounded-full px-4 py-2 transition-colors">
                      <Sparkles className="w-3.5 h-3.5" /> Reword
                    </button>
                    <button data-testid="humanize-chapter-button" disabled={busy}
                      onClick={() => onRewriteChapter(currentSpread.chapterNumber, "Make this sound genuinely human, natural and warm. Remove any stiff, robotic or AI-sounding phrasing and clichés while keeping the same meaning and length.")}
                      className="inline-flex items-center gap-2 font-ui text-xs font-medium text-slate-200 border border-gold-500/20 hover:border-emerald-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors">
                      <Wand2 className="w-3.5 h-3.5" /> Humanize
                    </button>
                  </>}
                  {!PUZZLE_MODES.includes(book.mode) && (
                    <button data-testid="add-chapter-button" onClick={() => setAddOpen(true)} disabled={busy}
                      className="inline-flex items-center gap-2 font-ui text-xs font-medium text-slate-200 border border-gold-500/20 hover:border-gold-400 disabled:opacity-40 rounded-full px-4 py-2 transition-colors">
                      <Plus className="w-3.5 h-3.5" /> {book.mode === "children" ? "Add page after" : "Add chapter after"}
                    </button>
                  )}
                  <button data-testid="delete-chapter-button" onClick={() => removeChapter(currentSpread.chapterNumber)} disabled={busy || book.chapters.length <= 1}
                    className="inline-flex items-center gap-2 font-ui text-xs font-medium text-slate-200 border border-gold-500/20 hover:border-red-500 hover:text-red-400 disabled:opacity-40 rounded-full px-4 py-2 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Page navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              data-testid="reader-page-prev"
              onClick={() => goTo((editing ? page : page) - 1)}
              disabled={page === 0}
              className="flex items-center gap-2 font-ui text-sm text-slate-300 border border-gold-500/12 hover:border-gold-400/60 disabled:opacity-30 rounded-lg px-4 py-2.5 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <div data-testid="reader-page-number" className="font-ui text-sm text-slate-400 text-center">
              <span className="text-slate-300 font-medium">{footerLabel}</span>
            </div>
            <button
              data-testid="reader-page-next"
              onClick={() => goTo(page + 1)}
              disabled={page >= navCount - 1}
              className="flex items-center gap-2 font-ui text-sm text-slate-300 border border-gold-500/12 hover:border-gold-400/60 disabled:opacity-30 rounded-lg px-4 py-2.5 transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </main>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-navy-900 border-gold-500/12 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a chapter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="font-ui text-xs uppercase tracking-wider text-slate-400/90">Chapter title</label>
              <input
                data-testid="add-chapter-title"
                className="w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-read text-slate-100 outline-none focus:border-gold-400"
                value={addTitle}
                placeholder="Leave blank to let the AI name it"
                onChange={(e) => setAddTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-ui text-xs uppercase tracking-wider text-slate-400/90">What should it cover?</label>
              <textarea
                data-testid="add-chapter-focus"
                className="w-full min-h-[90px] resize-none bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-read text-slate-100 outline-none focus:border-gold-400"
                value={addFocus}
                placeholder="e.g. A deeper look at how octopuses use tools"
                onChange={(e) => setAddFocus(e.target.value)}
              />
            </div>
            <p className="font-ui text-xs text-slate-500">
              It'll be inserted after {page >= 2 ? `Chapter ${chapterIndex + 1}` : "the last chapter"} and written in the book's voice.
            </p>
          </div>
          <DialogFooter>
            <button
              data-testid="add-chapter-submit"
              onClick={submitAddChapter}
              className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-lg px-4 py-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Write chapter
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rewriteOpen} onOpenChange={setRewriteOpen}>
        <DialogContent className="bg-navy-900 border-gold-500/12 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reword this {book.mode === "children" ? "page" : "chapter"}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="font-ui text-sm text-slate-400 -mt-2">
            Don't like how it sounds? Tell the AI how to rephrase it — same meaning, new voice.
          </DialogDescription>
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2">
              {["Make it warmer", "Simpler words", "More vivid", "Punchier & shorter", "Less formal", "More suspenseful"].map((p) => (
                <button
                  key={p}
                  data-testid={`reword-preset-${p.slice(0,6)}`}
                  onClick={() => setRewriteText((t) => (t ? t + "; " : "") + p.toLowerCase())}
                  className="font-ui text-xs text-slate-300 border border-gold-500/12 hover:border-gold-400/60 rounded-full px-3 py-1.5 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
            <textarea
              data-testid="reword-instruction"
              className="w-full min-h-[80px] resize-none bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-read text-slate-100 outline-none focus:border-gold-400"
              value={rewriteText}
              placeholder="e.g. make it sound like a cozy bedtime story, warmer and less robotic"
              onChange={(e) => setRewriteText(e.target.value)}
            />
          </div>
          <DialogFooter>
            <button
              data-testid="reword-submit"
              onClick={() => { const n = chapterIndex + 1; setRewriteOpen(false); onRewriteChapter(n, rewriteText); }}
              className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-lg px-4 py-2 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> Reword it
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={coverOpen} onOpenChange={setCoverOpen}>
        <DialogContent className="bg-navy-900 border-gold-500/12 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cover Studio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap items-center gap-3">
              <select
                data-testid="cover-style-select"
                value={coverStyle}
                onChange={(e) => setCoverStyle(e.target.value)}
                className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400"
              >
                {["Minimalist", "Painterly", "Photoreal", "Watercolor", "Vintage", "Bold Graphic", "Storybook"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                data-testid="cover-generate-options"
                onClick={() => onCoverOptions(coverStyle)}
                disabled={busy}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 disabled:opacity-40 text-stone-950 rounded-lg px-4 py-2 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate new cover
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(book.cover_options || []).map((oid) => (
                <button
                  key={oid}
                  data-testid="cover-option"
                  onClick={() => { onSelectCover(oid); setCoverOpen(false); }}
                  className={`rounded-lg overflow-hidden border-2 transition-colors ${(book.cover_art_id || book.cover_image_id) === oid ? "border-gold-400" : "border-transparent hover:border-gold-400/60"}`}
                >
                  <img src={`${imageUrl(oid)}?t=${oid}`} alt="cover option" className="w-full aspect-square object-cover" />
                </button>
              ))}
              {busy && (book.cover_options || []).length < 1 && (
                <div className="aspect-square rounded-lg bg-navy-800 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                </div>
              )}
            </div>
            <p className="font-ui text-xs text-slate-500">Tap an option to set it as your cover.</p>
            <CoverDesigns book={book} onApply={(i) => { onApplyDesign(i); setCoverOpen(false); }} />
            <SavedCovers book={book} onRestore={(id) => { onRestoreCover(id); setCoverOpen(false); }} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent data-testid="print-options-dialog" className="bg-navy-900 border-gold-500/12 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Print &amp; KDP size</DialogTitle>
            <DialogDescription className="font-read text-sm text-slate-400">
              Pick an Amazon KDP trim size and whether to include bleed (for edge-to-edge images).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="font-ui text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Trim size</label>
              <select
                data-testid="trim-size-select"
                value={trimKey}
                onChange={(e) => setTrimKey(e.target.value)}
                className="w-full bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400 [color-scheme:dark]"
              >
                {trims.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}{t.group === "children" ? " · picture books" : ""}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2.5 font-read text-sm text-slate-300 cursor-pointer">
              <input
                data-testid="bleed-toggle"
                type="checkbox"
                checked={bleed}
                onChange={(e) => setBleed(e.target.checked)}
                className="w-4 h-4 accent-gold-400"
              />
              Include 0.125&quot; bleed (recommended for full-bleed images)
            </label>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                data-testid="download-print-pdf"
                onClick={() => { getPremiumExport("pdf", { layout: "print", trim: trimKey, bleed }); setPrintOpen(false); }}
                disabled={!trims.length}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 disabled:opacity-40 text-stone-950 rounded-lg px-4 py-2 transition-colors"
              >
                <Printer className="w-4 h-4" /> Print PDF
              </button>
              <button
                data-testid="download-kdp-pack"
                onClick={() => { getPremiumExport("kdp", { trim: trimKey, bleed }); setPrintOpen(false); }}
                disabled={!trims.length}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-500 hover:bg-gold-400 disabled:opacity-40 text-white rounded-lg px-4 py-2 transition-colors"
              >
                <Package className="w-4 h-4" /> Publishing Kit (.zip)
              </button>
              <button
                data-testid="download-kdp-cover"
                onClick={() => { getPremiumExport("cover", { trim: trimKey, bleed }); setPrintOpen(false); }}
                disabled={!trims.length}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-navy-800 hover:bg-navy-700 disabled:opacity-40 text-slate-100 border border-gold-400/30 rounded-lg px-4 py-2 transition-colors"
              >
                <ImageIcon className="w-4 h-4" /> KDP Cover (1 file)
              </button>
              <button
                data-testid="download-readme"
                onClick={() => { getPremiumExport("readme"); setPrintOpen(false); }}
                className="flex items-center gap-2 font-ui text-sm font-medium bg-navy-800 hover:bg-navy-700 text-slate-100 border border-gold-400/30 rounded-lg px-4 py-2 transition-colors"
              >
                <FileText className="w-4 h-4" /> README
              </button>
              <WrapPreview bookId={book.id} trimKey={trimKey} />
            </div>
            <p className="font-ui text-xs text-slate-500">The <span className="text-slate-200">Publishing Kit .zip</span> includes your interior PDF, KDP cover, eBook, specs, and a branded KLYR README with step-by-step Amazon instructions and your book description. Digital PDF &amp; Word (free) always use a standard reading size. <span data-testid="paper-type-note" className="text-slate-300">Spine and cover dimensions are calculated for white paper: select White paper in KDP.</span></p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MarketingBar = ({ book, busy, onGenerate, onBookReplaced }) => {
  const kit = book.marketing && (book.marketing.generated_at || book.marketing.blurb) ? book.marketing : null;
  const m = kit;
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
  };
  return (
    <div
      data-testid="marketing-bar"
      className="mb-6 p-5 bg-navy-950/30 border border-navy-800/50 rounded-xl"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 font-ui text-sm text-slate-300">
          <Megaphone className="w-5 h-5" /> Amazon KDP Marketing Kit
        </div>
        <button
          data-testid="marketing-generate-button"
          onClick={onGenerate}
          disabled={busy}
          className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {m ? "Regenerate" : "Generate kit"}
        </button>
      </div>

      {book.author_bio && (
        <div className="mt-4 bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
          <div className="font-ui text-[11px] uppercase tracking-widest text-slate-400/80 mb-1">Author bio (yours, printed on the back cover)</div>
          <p data-testid="marketing-author-bio" className="font-read text-sm text-slate-300 leading-relaxed">{book.author_bio}</p>
        </div>
      )}

      {!m && !busy && (
        <p className="font-read text-sm text-slate-400 mt-2">
          Auto-write a back-cover blurb, tagline, high-intent keywords and KDP categories, ready to paste into your Amazon listing. Your author bio is only ever the one you write.
        </p>
      )}

      {m && (
        <div data-testid="marketing-content" className="grid md:grid-cols-2 gap-4 mt-4">
          {m.tagline && (
            <div className="md:col-span-2 bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
              <div className="font-ui text-[11px] uppercase tracking-widest text-slate-400/80 mb-1">Tagline</div>
              <p className="font-display text-lg text-slate-100 italic">“{m.tagline}”</p>
            </div>
          )}
          {m.blurb && (
            <div className="md:col-span-2 bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="font-ui text-[11px] uppercase tracking-widest text-slate-400/80">Amazon description</div>
                <button data-testid="copy-blurb" onClick={() => copy(m.blurb)} className="text-slate-500 hover:text-gold-200"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <p className="font-read text-sm text-slate-300 leading-relaxed whitespace-pre-line">{m.blurb}</p>
            </div>
          )}
          {m.back_cover_copy && (
            <div data-testid="back-cover-copy" className="md:col-span-2 bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="font-ui text-[11px] uppercase tracking-widest text-slate-400/80">Back-cover copy (hook, stakes, no spoilers)</div>
                <button data-testid="copy-back-cover" onClick={() => copy(m.back_cover_copy)} className="text-slate-500 hover:text-gold-200"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <p className="font-read text-sm text-slate-300 leading-relaxed whitespace-pre-line">{m.back_cover_copy}</p>
            </div>
          )}
          {m.keywords?.length > 0 && (
            <div className="bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
              <div className="flex items-center gap-1.5 font-ui text-[11px] uppercase tracking-widest text-slate-400/80 mb-2"><Tag className="w-3.5 h-3.5" /> Keywords</div>
              <div className="flex flex-wrap gap-2">
                {m.keywords.map((k) => (
                  <span key={k} className="font-ui text-xs text-slate-200 bg-navy-800/70 border border-gold-500/12 rounded-full px-3 py-1">{k}</span>
                ))}
              </div>
            </div>
          )}
          {m.categories?.length > 0 && (
            <div className="bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
              <div className="flex items-center gap-1.5 font-ui text-[11px] uppercase tracking-widest text-slate-400/80 mb-2"><FolderTree className="w-3.5 h-3.5" /> KDP categories</div>
              <ul className="space-y-1.5">
                {m.categories.map((c) => (
                  <li key={c} className="font-read text-sm text-slate-300">{c}</li>
                ))}
              </ul>
            </div>
          )}

        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <EndorsementEditor book={book} onSaved={onBookReplaced} />
      </div>
    </div>
  );
};


const CONTINUITY_FIX = "Fix continuity: connect this section smoothly to the previous one, remove anything introduced without setup, resolve any dangling thread, and keep it fully consistent with the rest of the book.";

const ContinuityBar = ({ book, busy, onRun, onGoToChapter, onRegenChapter, onRewriteChapter, onBookReplaced }) => {
  const v2 = book.continuity_report?.version >= 2;
  const [fixingAll, setFixingAll] = useState(false);
  const report = book.continuity_report;
  const rank = { major: 0, minor: 1 };
  const flags = [...(report?.flags || [])].sort((a, b) => (rank[a.severity] ?? 2) - (rank[b.severity] ?? 2) || a.number - b.number);
  const majorCount = flags.filter((f) => f.severity === "major").length;
  const unitCap = book.mode === "children" ? "Page" : "Chapter";
  const disabled = busy || fixingAll;
  const fix = async (n, kind) => {
    if (kind === "reword") await onRewriteChapter(n, CONTINUITY_FIX);
    else await onRegenChapter(n);
    if (onRun) onRun();
  };
  const fixAll = async () => {
    setFixingAll(true);
    try {
      const ordered = [...flags].sort((a, b) => a.number - b.number);
      for (const f of ordered) {
        await onRewriteChapter(f.number, CONTINUITY_FIX);
      }
      if (onRun) await onRun();
    } finally {
      setFixingAll(false);
    }
  };
  return (
    <div data-testid="continuity-bar" className="mb-6 p-5 bg-indigo-950/30 border border-indigo-800/40 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-ui text-sm text-indigo-300">
          <ShieldCheck className="w-5 h-5" /> Continuity check
        </div>
        <div className="flex items-center gap-2">
          {!v2 && flags.length > 0 && (
            <button
              data-testid="continuity-fix-all"
              onClick={fixAll}
              disabled={disabled}
              className="flex items-center gap-2 font-ui text-sm font-medium bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-stone-950 rounded-lg px-4 py-2 transition-colors"
            >
              {fixingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Fix entire book
            </button>
          )}
          <button
            data-testid="continuity-run-button"
            onClick={onRun}
            disabled={disabled}
            className="flex items-center gap-2 font-ui text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {report ? "Re-run check" : "Run check"}
          </button>
        </div>
      </div>

      {!report && !busy && (
        <p className="font-read text-sm text-slate-400 mt-3">
          Scan the whole book and flag any {book.mode === "children" ? "page" : "chapter"} that drifts — a character
          or object used before it&apos;s introduced, a jump in topic or setting, a contradiction, or a setup left
          hanging. Fix flagged sections with Reword or Regenerate before you publish.
        </p>
      )}

      {(busy || fixingAll) && (
        <p className="font-read text-sm text-indigo-300 mt-3 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {fixingAll ? "Fixing flagged sections one by one…" : (book.current_step || "Checking continuity…")}
        </p>
      )}

      {v2 && !busy && <ContinuityReportV2 book={book} onGoToChapter={onGoToChapter} onBook={onBookReplaced} />}
      {!v2 && report && !busy && !fixingAll && (
        <div className="mt-4" data-testid="continuity-report">
          {flags.length === 0 ? (
            <div data-testid="continuity-clean" className="flex items-center gap-2 font-read text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-800/40 rounded-xl px-4 py-3">
              <Check className="w-4 h-4" /> No drift detected — all {report.total} sections read as one continuous story.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="font-ui text-sm text-amber-300">
                {report.flagged} of {report.total} sections flagged{majorCount > 0 ? ` · ${majorCount} major (shown first)` : ""}.
              </div>
              {flags.map((f) => (
                <div key={f.number} data-testid={`continuity-flag-${f.number}`}
                  className="bg-navy-950/50 border border-gold-500/12 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${f.severity === "major" ? "text-red-400" : "text-amber-400"}`} />
                      <span className="font-ui text-sm font-semibold text-slate-100">{unitCap} {f.number}: {f.title}</span>
                      <span className={`font-ui text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${f.severity === "major" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
                        {f.severity}
                      </span>
                    </div>
                    <button
                      data-testid={`continuity-goto-${f.number}`}
                      onClick={() => onGoToChapter && onGoToChapter(f.number)}
                      className="font-ui text-xs text-indigo-300 hover:text-indigo-200 shrink-0"
                    >
                      Go to {unitCap.toLowerCase()} →
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {f.issues.map((iss, i) => {
                      const cat = typeof iss === "object" && iss ? iss.category : null;
                      const detail = typeof iss === "object" && iss ? iss.detail : iss;
                      return (
                        <li key={i} className="flex items-start gap-2 font-read text-sm text-slate-300">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                          <span>
                            {cat && (
                              <span className="font-ui text-[10px] uppercase tracking-wider text-indigo-200 bg-indigo-500/20 rounded px-1.5 py-0.5 mr-2 whitespace-nowrap">{cat}</span>
                            )}
                            {detail}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      data-testid={`continuity-fix-reword-${f.number}`}
                      onClick={() => fix(f.number, "reword")}
                      disabled={disabled}
                      className="flex items-center gap-1.5 font-ui text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Reword to fix
                    </button>
                    <button
                      data-testid={`continuity-fix-regen-${f.number}`}
                      onClick={() => fix(f.number, "regen")}
                      disabled={disabled}
                      className="flex items-center gap-1.5 font-ui text-xs font-medium text-slate-200 border border-gold-500/12 hover:border-indigo-400 disabled:opacity-40 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const AudiobookBar = ({ book, onGenerate }) => {
  const status = book.audio_status || "none";
  const [voice, setVoice] = useState(book.audio_voice || "onyx");
  const [readAlong, setReadAlong] = useState(book.mode === "children");
  const VOICES = ["onyx", "nova", "shimmer", "echo", "fable", "alloy", "sage", "coral", "ash"];

  return (
    <div
      data-testid="audiobook-bar"
      className="mb-6 p-4 bg-emerald-950/40 border border-emerald-800/50 rounded-xl flex flex-wrap items-center gap-4"
    >
      <div className="flex items-center gap-2 font-ui text-sm text-emerald-300">
        <Headphones className="w-5 h-5" /> Audiobook
      </div>

      {status === "generating" ? (
        <div className="flex-1 flex items-center gap-3 min-w-[200px]">
          <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
          <div className="flex-1 h-2 bg-navy-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${book.audio_progress || 0}%` }} />
          </div>
          <span className="font-mono2 text-xs text-emerald-300">{book.audio_progress || 0}%</span>
        </div>
      ) : status === "ready" ? (
        <div className="flex-1 flex flex-wrap items-center gap-3 min-w-[240px]">
          <audio data-testid="audiobook-player" controls src={`${api.audiobookUrl(book.id)}?t=${book.audio_progress}${book.audio_kind}`} className="h-9 flex-1 min-w-[200px]" />
          <span className="font-ui text-[11px] text-emerald-400/80">
            {book.audio_kind === "readalong" ? "Read-along w/ page chimes" : "Full narration"}
          </span>
          <a
            data-testid="audiobook-download"
            href={api.audiobookUrl(book.id)}
            className="flex items-center gap-2 font-ui text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 transition-colors"
          >
            <Download className="w-4 h-4" /> Download
          </a>
          <button
            data-testid="audiobook-regen"
            onClick={() => onGenerate(voice, readAlong)}
            className="flex items-center gap-2 font-ui text-xs text-emerald-300 border border-emerald-700 hover:border-emerald-500 rounded-lg px-3 py-2 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Redo
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="audiobook-voice"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-emerald-500"
          >
            {VOICES.map((v) => (
              <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 font-ui text-xs text-emerald-200 cursor-pointer">
            <input
              data-testid="audiobook-readalong"
              type="checkbox"
              checked={readAlong}
              onChange={(e) => setReadAlong(e.target.checked)}
              className="accent-emerald-500 w-4 h-4"
            />
            Read-along (gentle pace + page chimes)
          </label>
          <button
            data-testid="audiobook-generate"
            onClick={() => onGenerate(voice, readAlong)}
            className="flex items-center gap-2 font-ui text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 transition-colors"
          >
            <Play className="w-4 h-4" /> {readAlong ? "Make read-along" : "Narrate this book"}
          </button>
        </div>
      )}
    </div>
  );
};

const RerollButton = ({ onClick, busy, testid, label }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={busy}
    className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-gold-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
  >
    <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {label}
  </button>
);

const CoverPage = ({ book, editing, draft, setDraft, busy, onRegenCover, onCoverStudio, onSelectCover }) => (
  <div className="flex flex-col items-center text-center justify-center min-h-[60vh] gap-8">
    {book.cover_image_id && (
      <div className="relative">
        <img
          src={`${imageUrl(book.cover_image_id)}?t=${book.cover_image_id}`}
          alt="Book cover"
          className="w-64 h-64 sm:w-80 sm:h-80 object-cover rounded-lg shadow-2xl"
        />
      </div>
    )}
    {!editing && (
      <div className="flex flex-wrap items-center justify-center gap-2">
        <RerollButton onClick={onRegenCover} busy={busy} testid="reroll-cover-button" label="Reroll cover" />
        <button
          data-testid="cover-studio-button"
          onClick={onCoverStudio}
          disabled={busy}
          className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-gold-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" /> Cover Studio
        </button>
      </div>
    )}
    <div className="w-full max-w-xl">
      {editing ? (
        <div className="space-y-3">
          <input
            data-testid="edit-title-input"
            className={`${editInput} font-display text-3xl font-bold text-center`}
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          />
          <input
            data-testid="edit-subtitle-input"
            className={`${editInput} font-read italic text-center`}
            value={draft.subtitle}
            placeholder="Subtitle / tagline"
            onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
          />
          <input
            data-testid="edit-author-input"
            className={`${editInput} font-ui text-sm text-center`}
            value={draft.author}
            placeholder="Author / pen name (e.g. by Jane Doe)"
            onChange={(e) => setDraft((d) => ({ ...d, author: e.target.value }))}
          />
          <textarea
            data-testid="edit-author-bio-input"
            className={`${editInput} font-ui text-sm`}
            rows={3}
            maxLength={700}
            value={draft.author_bio}
            placeholder="About the author (optional). Printed on the back cover word for word"
            onChange={(e) => setDraft((d) => ({ ...d, author_bio: e.target.value }))}
          />
        </div>
      ) : (
        <>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            {book.title}
          </h1>
          {book.subtitle && (
            <p className="font-read italic text-lg sm:text-xl opacity-70 mt-5">{book.subtitle}</p>
          )}
        </>
      )}
      <div className="mt-8 h-px w-24 mx-auto rule border-t" />
      {book.author && <p className="font-ui text-sm opacity-60 mt-6">{book.author}</p>}
      <p className="font-ui text-xs opacity-40 mt-4">
        {book.mode === "children"
          ? `${book.chapters.length} illustrated pages`
          : PUZZLE_MODES.includes(book.mode)
            ? `${book.chapters.length} puzzles`
            : ACTIVITY_MODES.includes(book.mode)
              ? `${book.chapters.length} pages`
              : `${book.word_count?.toLocaleString()} words · ${book.chapters.length} chapters`}
      </p>
      {book.diagram_svg && (
        <div data-testid="framework-diagram" className="mt-10 max-w-xl mx-auto bg-white rounded-2xl p-4 shadow-xl"
          dangerouslySetInnerHTML={{ __html: book.diagram_svg }} />
      )}
    </div>
  </div>
);

const TocPage = ({ book, onGo }) => (
  <div className="min-h-[60vh]">
    <div className="flex items-center gap-3 mb-10">
      <BookMarked className="w-6 h-6 opacity-60" />
      <h2 className="font-display text-3xl sm:text-4xl font-bold">Table of Contents</h2>
    </div>
    <div className="space-y-1">
      {book.chapters.map((ch, i) => (
        <button
          key={ch.number}
          onClick={() => onGo(i)}
          className="w-full group flex items-baseline gap-4 py-3.5 border-b rule border-b-current/10 text-left"
        >
          <span className="font-display text-2xl font-semibold opacity-40 w-10">{ch.number}</span>
          <span className="font-read text-lg group-hover:opacity-100 opacity-80 flex-1">{ch.title}</span>
          <span className="flex-1 border-b border-dotted opacity-20 mx-2" />
        </button>
      ))}
    </div>
  </div>
);

// ---- Book Preview: physical page renderers ----
// Covers KLYR has already typeset server-side (activity books & fallback plates) must not get a second title.
const ACTIVITY_COVER_MODES = ["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook"];
const ComposedCover = ({ book, dims }) => {
  const scale = dims.scale;
  const typeset = !!book.cover_image_id && (book.cover_typeset || book.cover_status === "fallback"
    || (!!book.cover_status && ACTIVITY_COVER_MODES.includes(book.mode)));
  return (
    <div className="kbp-cover" data-testid="cover-page" style={{ width: dims.pageW, height: dims.pageH }}>
      {book.cover_image_id ? (
        <img className="art" src={`${imageUrl(book.cover_image_id)}?t=${book.cover_image_id}`} alt="Book cover" />
      ) : (
        <div className="art" style={{ background: "linear-gradient(160deg,#0f1b3d,#1a2a55 55%,#0b1226)" }} />
      )}
      {!typeset && <div className="scrim" />}
      {!typeset && <div className="cov-inner" style={{ padding: `${dims.margin}px ${dims.margin}px ${dims.margin * 1.1}px` }}>
        <div className="cov-title" style={{ fontSize: scale * 30 / 72, marginBottom: scale * 8 / 72 }}>{book.title}</div>
        {book.subtitle && (
          <>
            <div className="cov-rule" style={{ width: scale * 0.9, margin: `${scale * 6 / 72}px auto` }} />
            <div className="cov-sub" style={{ fontSize: scale * 14 / 72 }}>{book.subtitle}</div>
          </>
        )}
        {book.author && (
          <div className="cov-author" style={{ fontSize: scale * 12 / 72, marginTop: scale * 20 / 72 }}>{book.author}</div>
        )}
      </div>}
    </div>
  );
};

const PreviewSpread = ({ spread, dims, theme, book }) => {
  if (!spread) return null;
  if (spread.type === "cover") return <div className="flex justify-center"><ComposedCover book={book} dims={dims} /></div>;

  const frameStyle = { width: dims.pageW, height: dims.pageH };
  const padStyle = { padding: dims.margin };
  const folio = (
    <div className="kbp-folio" style={{ height: dims.footer, lineHeight: `${dims.footer}px`, fontSize: dims.bodyPx * 0.82 }}>
      {spread.manuscriptPage}
    </div>
  );

  if (spread.type === "toc") {
    return (
      <div className="flex justify-center">
        <div className={`${theme} kbp-page`} style={frameStyle}>
          <div style={padStyle} className="h-full">
            <div className="kbp" style={{ fontSize: dims.bodyPx, lineHeight: 1.5 }}>
              <div style={{ textAlign: "center", fontWeight: 700, fontSize: dims.bodyPx * 1.6, marginBottom: dims.bodyPx * 1.4 }}>
                Contents
              </div>
              <div style={{ maxHeight: dims.textH - dims.bodyPx * 3, overflow: "hidden" }}>
                {spread.entries.map((e) => (
                  <div key={e.number} style={{ display: "flex", alignItems: "baseline", gap: 8, margin: `${dims.bodyPx * 0.5}px 0` }}>
                    <span style={{ opacity: 0.55, width: dims.bodyPx * 1.6 }}>{e.number}</span>
                    <span style={{ flex: "0 1 auto" }}>{e.title}</span>
                    <span style={{ flex: 1, borderBottom: "1px dotted currentColor", opacity: 0.25, margin: "0 6px" }} />
                    <span style={{ opacity: 0.6 }}>{e.page}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // children page: full illustration + large centred text
  if (spread.mode === "children") {
    return (
      <div className="flex justify-center">
        <div className={`${theme} kbp-page`} style={frameStyle}>
          <div style={padStyle} className="h-full flex flex-col">
            {spread.image_id && (
              <img src={`${imageUrl(spread.image_id)}?t=${spread.image_id}`} alt=""
                style={{ width: "100%", height: dims.pageH * 0.52, objectFit: "cover", borderRadius: 4, marginBottom: dims.margin * 0.5 }} />
            )}
            <div className="kbp" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div data-testid="children-spread-text" style={{ fontSize: dims.bodyPx * 1.7, lineHeight: 1.5, textAlign: "center", whiteSpace: "pre-line" }}>
                {stripMd(spread.content)}
              </div>
            </div>
            {folio}
          </div>
        </div>
      </div>
    );
  }

  if (spread.mode === "puzzle") {
    const chapter = book.chapters[spread.chapterIndex];
    return (
      <div className="flex justify-center">
        <div className={`${theme} kbp-page`} style={frameStyle}>
          <div style={padStyle} className="h-full overflow-hidden">
            <PuzzleView chapter={chapter} />
            {folio}
          </div>
        </div>
      </div>
    );
  }

  // prose page
  return (
    <div className="flex justify-center">
      <div className={`${theme} kbp-page`} style={frameStyle} data-testid="preview-prose-page">
        <div style={padStyle} className="h-full">
          <div className="kbp kbp-flow"
            style={{ height: dims.textH, fontSize: dims.bodyPx, lineHeight: `${dims.linePx}px` }}
            dangerouslySetInnerHTML={{ __html: spread.html }} />
          {folio}
        </div>
      </div>
    </div>
  );
};

const ChapterPage = ({ chapter, editing, draft, setChapterField, busy, onRegenChapter, onAdd, onDelete, canDelete, isChildren, onRewrite, onHumanize }) => {
  if (!chapter) return null;
  const d = editing && draft ? draft.chapters[chapter.number] : null;
  return (
    <article className={`reader-prose mx-auto ${isChildren ? "max-w-3xl" : "max-w-2xl"}`}>
      <div className="text-center mb-6">
        <div className="font-ui text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">
          {isChildren ? `Page ${chapter.number}` : `Chapter ${chapter.number}`}
        </div>
        {editing ? (
          <input
            data-testid="edit-chapter-title"
            className={`${editInput} font-display text-3xl font-bold text-center`}
            value={d.title}
            onChange={(e) => setChapterField(chapter.number, "title", e.target.value)}
          />
        ) : (
          !isChildren && (
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-6">
              {chapter.title}
            </h2>
          )
        )}
        {!isChildren && <div className="h-px w-16 mx-auto rule border-t mt-6" />}
        {editing && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <button
              data-testid="edit-add-chapter-button"
              onClick={onAdd}
              disabled={busy}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-gold-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {isChildren ? "Add page after" : "Add chapter after"}
            </button>
            <button
              data-testid="edit-delete-chapter-button"
              onClick={() => onDelete(chapter.number)}
              disabled={busy || !canDelete}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-red-500 hover:text-red-600 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete this {isChildren ? "page" : "chapter"}
            </button>
          </div>
        )}
        {!editing && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <RerollButton
              onClick={() => onRegenChapter(chapter.number)}
              busy={busy}
              testid="reroll-chapter-button"
              label="Reroll"
            />
            <button
              data-testid="reword-chapter-button"
              onClick={onRewrite}
              disabled={busy}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-gold-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" /> Reword
            </button>
            <button
              data-testid="humanize-chapter-button"
              onClick={onHumanize}
              disabled={busy}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-emerald-600 hover:text-emerald-700 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Wand2 className="w-3.5 h-3.5" /> Humanize
            </button>
            <button
              data-testid="add-chapter-button"
              onClick={onAdd}
              disabled={busy}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-gold-500 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {isChildren ? "Add page after" : "Add chapter after"}
            </button>
            <button
              data-testid="delete-chapter-button"
              onClick={() => onDelete(chapter.number)}
              disabled={busy || !canDelete}
              className="inline-flex items-center gap-2 font-ui text-xs font-medium bg-black/5 border border-current/20 hover:border-red-500 hover:text-red-600 disabled:opacity-40 rounded-full px-4 py-2 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}
      </div>

      {chapter.image_id && (
        <figure className="mb-8">
          <img
            src={`${imageUrl(chapter.image_id)}?t=${chapter.image_id}`}
            alt={chapter.title}
            className={`w-full rounded-2xl shadow-lg object-cover ${isChildren ? "h-72 sm:h-96" : "h-56 sm:h-72"}`}
          />
          {editing ? (
            <input
              data-testid="edit-chapter-caption"
              className={`${editInput} text-sm italic text-center mt-3`}
              value={d.caption}
              placeholder="Image caption"
              onChange={(e) => setChapterField(chapter.number, "caption", e.target.value)}
            />
          ) : (
            !isChildren && chapter.caption && (
              <figcaption className="text-center text-sm italic opacity-60 mt-3 font-read">
                {chapter.caption}
              </figcaption>
            )
          )}
        </figure>
      )}

      {editing ? (
        <textarea
          data-testid="edit-chapter-content"
          className={`${editInput} font-read leading-relaxed resize-y ${isChildren ? "text-2xl min-h-[160px] text-center" : "text-base min-h-[400px]"}`}
          value={d.content}
          onChange={(e) => setChapterField(chapter.number, "content", e.target.value)}
        />
      ) : isChildren ? (
        <>
          <div
            data-testid="children-page-text"
            className="font-read text-2xl sm:text-3xl leading-relaxed text-center max-w-2xl mx-auto whitespace-pre-line"
          >
            {stripMd(chapter.content)}
          </div>
          {!chapter.image_id && (chapter.content || "").trim().length < 140 && <BrandFlourish />}
        </>
      ) : (
        <div className="dropcap">{renderContent(stripDupHeading(chapter.content, chapter.title))}</div>
      )}
    </article>
  );
};
